import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Grid,
  Autocomplete,
} from '@mui/material';
import {
  Agriculture as AgricultureIcon,
} from '@mui/icons-material';
import { DialogHeader } from '../Common';
import { Farm, CreateFarmDto, UpdateFarmDto } from '../../types/farms';
import { Farmer } from '../../types/farmers';
import type { DuplicateMatch } from '../../types/duplicateMatch';
import { farmService } from '../../services/farmService';
import { farmerService } from '../../services/farmerService';
import { useDuplicateCheck } from '../../hooks/useDuplicateCheck';
import { DuplicateMatchPanel } from '../shared/DuplicateMatchPanel';
import { useSnackbar } from 'notistack';

const MAX_PAGE_SIZE = 1000;

interface FarmDialogProps {
  open: boolean;
  farm: Farm | null;
  onClose: (saved: boolean) => void;
  onCreated?: (farm: Farm) => void;
  preselectedFarmerId?: number;
  /**
   * Disable the farmer Autocomplete (it still shows the preselected / current farmer). Opened from the
   * Farm Workspace so an Edit cannot silently reparent — "Move…" is the only reparent path.
   */
  lockParent?: boolean;
  /** Show the create-time duplicate "did you mean?" nudge. Default true; never blocks create. */
  enableDuplicateCheck?: boolean;
  /**
   * "Use this instead" on a duplicate match. When provided it is called with the picked match and the
   * dialog closes without creating; when omitted the dialog just closes. Does not navigate.
   */
  onUseExisting?: (match: DuplicateMatch) => void;
}

const FarmDialog: React.FC<FarmDialogProps> = ({
  open,
  farm,
  onClose,
  onCreated,
  preselectedFarmerId,
  lockParent = false,
  enableDuplicateCheck = true,
  onUseExisting,
}) => {
  const [formData, setFormData] = useState<CreateFarmDto>({
    farmName: '',
    country: '',
    idFarmer: undefined,
  });
  const [farmers, setFarmers] = useState<Farmer[]>([]);
  const [selectedFarmer, setSelectedFarmer] = useState<Farmer | null>(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { enqueueSnackbar } = useSnackbar();

  // Load farmers
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    // Editing → the farm's own farmer; creating → the preselected one (Farm Workspace "+ Add farm").
    const targetFarmerId = farm?.idFarmer || preselectedFarmerId;
    const loadFarmers = async () => {
      try {
        if (lockParent && targetFarmerId) {
          // A locked picker only ever shows this one farmer — fetch it, not the 1,000-row list.
          const farmer = await farmerService.getById(targetFarmerId);
          if (cancelled) return;
          setFarmers([farmer]);
          setSelectedFarmer(farmer);
          return;
        }
        const response = await farmerService.getFarmers({ pageSize: MAX_PAGE_SIZE });
        if (cancelled) return;
        setFarmers(response.items);
        if (targetFarmerId) {
          const farmer = response.items.find(f => f.idFarmer === targetFarmerId);
          if (farmer) setSelectedFarmer(farmer);
        }
      } catch {
        if (!cancelled) enqueueSnackbar('Failed to load farmers', { variant: 'error' });
      }
    };
    loadFarmers();
    return () => {
      cancelled = true;
    };
  }, [open, farm, preselectedFarmerId, lockParent, enqueueSnackbar]);

  useEffect(() => {
    // Reset the farmer selection on every open; the load above re-selects it once it lands.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- resets dialog form state when the target farm or open state changes; refactor to render-time derivation is not trivially safe
    setSelectedFarmer(null);
    if (farm) {
      setFormData({
        farmName: farm.farmName,
        country: farm.country || '',
        idFarmer: farm.idFarmer,
      });
    } else {
      setFormData({
        farmName: '',
        country: '',
        idFarmer: preselectedFarmerId || undefined,
      });
    }
    setErrors({});
  }, [farm, open, preselectedFarmerId]);

  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.farmName.trim()) {
      newErrors.farmName = 'Farm name is required';
    } else if (formData.farmName.trim().length < 2) {
      newErrors.farmName = 'Farm name must be at least 2 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      if (farm) {
        await farmService.update(farm.idFarm, formData as UpdateFarmDto);
        enqueueSnackbar('Farm updated successfully', { variant: 'success' });
      } else {
        const created = await farmService.create(formData);
        enqueueSnackbar('Farm created successfully', { variant: 'success' });
        onClose(true);
        onCreated?.(created);
        return;
      }
      onClose(true);
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage = axiosError.response?.data?.message || axiosError.message || 'Failed to save farm';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose(false);
    }
  };

  // Create-time duplicate "did you mean?" nudge (Plan C, Layer 1). Only in create mode; fires once a
  // farmer is selected AND the name is ≥ 2 chars — so it no-ops in inline-create contexts with no
  // parent chosen yet. Never blocks the Create button.
  const isCreate = !farm;
  const dupFarmName = formData.farmName.trim();
  const dupFarmerId = formData.idFarmer;
  const dupSignature = useMemo(() => {
    if (!isCreate || !enableDuplicateCheck) return null;
    if (!dupFarmerId || dupFarmName.length < 2) return null;
    return `${dupFarmerId}|${dupFarmName}`;
  }, [isCreate, enableDuplicateCheck, dupFarmerId, dupFarmName]);

  const { matches: dupMatches, dismissed: dupDismissed, dismiss: dismissDup } = useDuplicateCheck(
    dupSignature,
    () =>
      dupFarmerId
        ? farmService.checkDuplicates({ farmerId: dupFarmerId, name: dupFarmName, excludeId: farm?.idFarm })
        : Promise.resolve<DuplicateMatch[]>([]),
  );

  const handleUseExisting = (match: DuplicateMatch) => {
    onUseExisting?.(match);
    onClose(false);
  };

  const showDupNudge = dupMatches.length > 0 && !dupDismissed;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: { borderRadius: 2 }
        }
      }}
    >
      <DialogHeader
        title={farm ? 'Edit Farm' : 'Add New Farm'}
        icon={<AgricultureIcon sx={{ color: 'primary.main' }} />}
        onClose={handleClose}
        disabled={loading}
      />
      <DialogContent dividers sx={{ pt: 3 }}>
        <Grid container spacing={3}>
          <Grid size={12}>
            <TextField
              label="Farm Name"
              fullWidth
              value={formData.farmName}
              onChange={(e) => {
                setFormData({ ...formData, farmName: e.target.value });
                if (errors.farmName) {
                  setErrors({ ...errors, farmName: '' });
                }
              }}
              error={!!errors.farmName}
              helperText={errors.farmName || 'Required'}
              disabled={loading}
              autoFocus
              required
              slotProps={{
                input: {
                  sx: { borderRadius: 2 }
                }
              }}
            />
          </Grid>

          <Grid size={12}>
            <Autocomplete
              options={farmers}
              getOptionLabel={(option) => option.fullName}
              value={selectedFarmer}
              onChange={(event, newValue) => {
                setSelectedFarmer(newValue);
                setFormData({ ...formData, idFarmer: newValue?.idFarmer });
              }}
              disabled={loading || lockParent}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Farmer"
                  helperText="Optional - Select the farmer who owns this farm"
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      sx: { borderRadius: 2 }
                    }
                  }}
                />
              )}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              label="Country"
              fullWidth
              value={formData.country}
              onChange={(e) => {
                setFormData({ ...formData, country: e.target.value });
              }}
              disabled={loading}
              helperText="Optional - Farm location country"
              slotProps={{
                input: {
                  sx: { borderRadius: 2 }
                }
              }}
            />
          </Grid>
        </Grid>

        {showDupNudge && (
          <Box sx={{ mt: 2 }}>
            <DuplicateMatchPanel
              matches={dupMatches}
              entityNoun="farm"
              childNoun="plot"
              onUseExisting={handleUseExisting}
              onDismiss={dismissDup}
            />
          </Box>
        )}

        {farm && (
          <Box sx={(theme) => ({
            mt: 3,
            p: 2,
            backgroundColor: 'grey.50',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            ...theme.applyStyles('dark', { backgroundColor: 'grey.800' }),
          })}>
            <Typography variant="caption" color="text.secondary" display="block">
              Farm ID: #{farm.idFarm}
            </Typography>
            {farm.createdAt && (
              <Typography variant="caption" color="text.secondary" display="block">
                Created: {new Date(farm.createdAt).toLocaleString()}
              </Typography>
            )}
            {farm.updatedAt && (
              <Typography variant="caption" color="text.secondary" display="block">
                Last Updated: {new Date(farm.updatedAt).toLocaleString()}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Plots: {farm.plotCount} | Samples: {farm.sampleCount}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2.5, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{
            textTransform: 'none',
            borderRadius: 1.5,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.farmName.trim()}
          sx={{
            textTransform: 'none',
            borderRadius: 1.5,
            minWidth: 100,
          }}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            farm ? 'Update' : 'Create'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FarmDialog;
